import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useWorkspace } from '../context/WorkspaceContext';
import API_BASE from '../config/api';

const STATUS_OPTIONS = ['Proposal', 'Confirmed', 'Active', 'Completed', 'Archived'];

const INPUT_STYLE = {
  width: '100%',
  backgroundColor: '#1a1a1a',
  border: '1px solid #3a3a3a',
  borderRadius: '4px',
  color: '#F5F5F5',
  fontFamily: "'Roboto', sans-serif",
  fontSize: '14px',
  padding: '9px 12px',
  outline: 'none',
  boxSizing: 'border-box',
};

const LABEL_STYLE = {
  fontFamily: "'Roboto', sans-serif",
  fontWeight: 500,
  fontSize: '11px',
  color: '#888888',
  letterSpacing: '1px',
  marginBottom: '6px',
  display: 'block',
};

export default function AddEventModal({ isOpen, onClose, onSuccess, projectId, projectName }) {
  const { token } = useAuth();
  const { activeWorkspace } = useWorkspace();

  const [title, setTitle] = useState('');
  const [status, setStatus] = useState('Proposal');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [location, setLocation] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setTitle('');
      setStatus('Proposal');
      setStartDate('');
      setEndDate('');
      setLocation('');
      setError('');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  async function handleSubmit(e) {
    e.preventDefault();
    if (!title.trim()) { setError('Event name is required'); return; }
    if (!activeWorkspace) { setError('No active workspace'); return; }

    if (!projectId) {
      setError('Please create or select a project first. Events must belong to a project.');
      return;
    }

    setIsSubmitting(true);
    setError('');
    try {
      const res = await axios.post(
        `${API_BASE}/api/events`,
        {
          title: title.trim(),
          project_id: projectId,
          workspace_id: activeWorkspace._id,
          status,
          start_date: startDate || undefined,
          end_date: endDate || undefined,
          location: location.trim() || undefined,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onSuccess(res.data);
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create event');
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0,0,0,0.7)',
        zIndex: 2000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        style={{
          backgroundColor: '#2a2a2a',
          border: '1px solid #3a3a3a',
          borderRadius: '8px',
          width: '480px',
          maxWidth: '95vw',
          padding: '28px 32px',
          boxShadow: '0 24px 64px rgba(0,0,0,0.6)',
        }}
      >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <h2 style={{ fontFamily: "'Roboto', sans-serif", fontWeight: 600, fontSize: '20px', color: '#F5F5F5', margin: 0 }}>
            New Event
          </h2>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', color: '#565656', fontSize: '22px', cursor: 'pointer', lineHeight: 1, padding: '0 4px' }}
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Row 1: Title */}
          <div style={{ marginBottom: '18px' }}>
            <label style={LABEL_STYLE}>EVENT NAME *</label>
            <input
              autoFocus
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Boston - Agganis Arena"
              style={INPUT_STYLE}
            />
          </div>

          {/* Row 2: Project (read-only) */}
          <div style={{ marginBottom: '18px' }}>
            <label style={LABEL_STYLE}>PROJECT</label>
            <div
              style={{
                backgroundColor: '#222222',
                border: '1px solid #3a3a3a',
                borderRadius: '4px',
                color: '#F5F5F5',
                fontFamily: "'Roboto', sans-serif",
                fontSize: '14px',
                padding: '9px 12px',
              }}
            >
              {projectName || '—'}
            </div>
          </div>

          {/* Row 3: Status + Start Date */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '18px' }}>
            <div>
              <label style={LABEL_STYLE}>STATUS</label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                style={{ ...INPUT_STYLE, cursor: 'pointer' }}
              >
                {STATUS_OPTIONS.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={LABEL_STYLE}>START DATE</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                style={{ ...INPUT_STYLE, colorScheme: 'dark' }}
              />
            </div>
          </div>

          {/* Row 4: End Date */}
          <div style={{ marginBottom: '18px' }}>
            <label style={LABEL_STYLE}>END DATE</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              style={{ ...INPUT_STYLE, colorScheme: 'dark' }}
            />
          </div>

          {/* Row 5: Location */}
          <div style={{ marginBottom: '24px' }}>
            <label style={LABEL_STYLE}>LOCATION</label>
            <input
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="City, State"
              style={INPUT_STYLE}
            />
          </div>

          {error && (
            <div style={{ fontFamily: "'Roboto', sans-serif", fontSize: '13px', color: '#BF2634', marginBottom: '16px' }}>
              {error}
            </div>
          )}

          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                background: 'transparent',
                border: '1px solid #565656',
                color: '#565656',
                fontFamily: "'Roboto', sans-serif",
                fontWeight: 500,
                fontSize: '14px',
                padding: '9px 20px',
                borderRadius: '4px',
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              style={{
                backgroundColor: isSubmitting ? '#888888' : '#BF2634',
                border: 'none',
                color: '#FFFFFF',
                fontFamily: "'Roboto', sans-serif",
                fontWeight: 500,
                fontSize: '14px',
                padding: '9px 24px',
                borderRadius: '4px',
                cursor: isSubmitting ? 'default' : 'pointer',
              }}
            >
              {isSubmitting ? 'Creating...' : 'Create Event'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
